import React, { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useBranch } from "@/contexts/BranchContext";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * SilviaButton — Floating AI assistant for client portal.
 * Premium concierge experience with a voice-reactive equalizer orb.
 */
export default function SilviaButton({ sidebarCollapsed }) {
    const navigate = useNavigate();
    const { selectedBranchId } = useBranch();
    const [state, setState] = useState("idle");
    const [active, setActive] = useState(false);
    const audioQueueRef = useRef([]);
    const isPlayingRef = useRef(false);
    const currentAudioRef = useRef(null);
    const recognitionRef = useRef(null);
    const abortRef = useRef(null);
    const stateRef = useRef("idle");
    const navTargetRef = useRef(null);
    // Session counter — incremented on each activation. All async ops check this
    // to bail out if the session was stopped/restarted while they were in-flight.
    const sessionRef = useRef(0);
    const ttsAbortRef = useRef(null);

    // Audio-reactive visualizer refs
    const canvasRef = useRef(null);
    const rafRef = useRef(null);
    const audioCtxRef = useRef(null);
    const analyserRef = useRef(null);
    const freqDataRef = useRef(null);
    // Microphone analyser (reacts to the user's own voice while listening)
    const micStreamRef = useRef(null);
    const micSourceRef = useRef(null);
    const micAnalyserRef = useRef(null);
    const micDataRef = useRef(null);

    useEffect(() => { stateRef.current = state; }, [state]);
    useEffect(() => { return () => killSession(); }, []); // cleanup on unmount

    const getToken = () => localStorage.getItem("trulyn_token");

    /** Lazily build a shared AudioContext + AnalyserNode wired to the speakers. */
    const ensureAudioGraph = useCallback(() => {
        try {
            if (!audioCtxRef.current) {
                const Ctx = window.AudioContext || window.webkitAudioContext;
                if (!Ctx) return null;
                const ctx = new Ctx();
                const analyser = ctx.createAnalyser();
                analyser.fftSize = 256;
                analyser.smoothingTimeConstant = 0.8;
                analyser.connect(ctx.destination);
                audioCtxRef.current = ctx;
                analyserRef.current = analyser;
                freqDataRef.current = new Uint8Array(analyser.frequencyBinCount);
            }
            if (audioCtxRef.current.state === "suspended") {
                audioCtxRef.current.resume().catch(() => {});
            }
            return audioCtxRef.current;
        } catch (_e) { return null; }
    }, []);

    /** Start a dedicated mic analyser so the orb reacts to the user's real voice. */
    const startMicAnalyser = useCallback(async (sid) => {
        if (micAnalyserRef.current || micStreamRef.current) return;
        try {
            const ctx = ensureAudioGraph();
            if (!ctx || !navigator.mediaDevices?.getUserMedia) return;
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            if (sessionRef.current !== sid) { stream.getTracks().forEach((t) => t.stop()); return; }
            const source = ctx.createMediaStreamSource(stream);
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 256;
            analyser.smoothingTimeConstant = 0.8;
            source.connect(analyser); // NOT wired to destination — avoids audio feedback
            micStreamRef.current = stream;
            micSourceRef.current = source;
            micAnalyserRef.current = analyser;
            micDataRef.current = new Uint8Array(analyser.frequencyBinCount);
        } catch (_e) { /* mic denied/unavailable — synthetic animation fallback */ }
    }, [ensureAudioGraph]);

    /** Release the microphone stream and analyser. */
    const stopMicAnalyser = useCallback(() => {
        if (micStreamRef.current) {
            micStreamRef.current.getTracks().forEach((t) => t.stop());
            micStreamRef.current = null;
        }
        if (micSourceRef.current) {
            try { micSourceRef.current.disconnect(); } catch (_e) { /* */ }
            micSourceRef.current = null;
        }
        micAnalyserRef.current = null;
        micDataRef.current = null;
    }, []);

    /** Play a short beep via Web Audio API to signal "your turn" */
    const playBeep = useCallback(() => {
        try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.frequency.value = 880;
            osc.type = "sine";
            gain.gain.setValueAtTime(0.15, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);
            osc.start(ctx.currentTime);
            osc.stop(ctx.currentTime + 0.2);
        } catch (_e) { /* no audio context */ }
    }, []);

    const killSession = () => {
        sessionRef.current += 1; // invalidate all in-flight async work
        stopMicAnalyser();
        if (recognitionRef.current) {
            try { recognitionRef.current.abort(); } catch (_e) { /* */ }
            recognitionRef.current = null;
        }
        if (currentAudioRef.current) {
            currentAudioRef.current.pause();
            currentAudioRef.current = null;
        }
        if (abortRef.current) {
            try { abortRef.current.abort(); } catch (_e) { /* */ }
        }
        if (ttsAbortRef.current) {
            try { ttsAbortRef.current.abort(); } catch (_e) { /* */ }
        }
        audioQueueRef.current = [];
        isPlayingRef.current = false;
    };

    const playNextInQueue = useCallback(async (sid) => {
        if (sessionRef.current !== sid) return;
        if (isPlayingRef.current || audioQueueRef.current.length === 0) {
            if (!isPlayingRef.current && audioQueueRef.current.length === 0 && stateRef.current === "speaking") {
                if (sessionRef.current !== sid) return;
                startListening(sid);
            }
            return;
        }
        isPlayingRef.current = true;
        setState("speaking");

        const audioBytes = audioQueueRef.current.shift();
        try {
            const blob = new Blob([audioBytes], { type: "audio/mpeg" });
            const url = URL.createObjectURL(blob);
            const audio = new Audio(url);
            currentAudioRef.current = audio;
            // Route this clip through the analyser so the orb reacts to Silvia's real voice.
            try {
                const ctx = ensureAudioGraph();
                if (ctx && analyserRef.current) {
                    const source = ctx.createMediaElementSource(audio);
                    source.connect(analyserRef.current);
                }
            } catch (_e) { /* fallback: audio plays via element default output */ }
            await new Promise((resolve, reject) => {
                audio.onended = () => { URL.revokeObjectURL(url); resolve(); };
                audio.onerror = () => { URL.revokeObjectURL(url); reject(); };
                audio.play().catch(reject);
            });
        } catch (_e) { /* audio error */ }

        currentAudioRef.current = null;
        isPlayingRef.current = false;

        // Navigate after first audio clip finishes (the "intro" sentence)
        if (navTargetRef.current && sessionRef.current === sid) {
            const target = navTargetRef.current;
            navTargetRef.current = null;
            navigate(target);
        }

        if (sessionRef.current === sid) playNextInQueue(sid);
    }, [navigate, ensureAudioGraph]);

    /** Clean markdown artifacts before sending to TTS */
    const cleanForVoice = (text) => {
        return text
            .replace(/\*\*(.+?)\*\*/g, "$1")   // **bold** → bold
            .replace(/\*(.+?)\*/g, "$1")         // *italic* → italic
            .replace(/^[\s]*[-•]\s+/gm, "")      // bullet points
            .replace(/^[\s]*\d+\.\s+/gm, "")     // numbered lists
            .replace(/#{1,6}\s+/g, "")            // headings
            .replace(/\n+/g, " ")                 // newlines → space
            .replace(/\s{2,}/g, " ")              // collapse whitespace
            .trim();
    };

    const enqueueTTS = useCallback(async (text, sid) => {
        const clean = cleanForVoice(text);
        if (!clean || sessionRef.current !== sid) return;
        const token = getToken();
        const controller = new AbortController();
        ttsAbortRef.current = controller;
        try {
            const resp = await fetch(`${API_BASE}/api/ai/silvia/tts`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({ text: clean }),
                signal: controller.signal,
            });
            if (!resp.ok || sessionRef.current !== sid) return;
            const audioBytes = await resp.arrayBuffer();
            if (sessionRef.current !== sid) return; // check again after await
            audioQueueRef.current.push(audioBytes);
            playNextInQueue(sid);
        } catch (_e) { /* TTS error or aborted */ }
    }, [playNextInQueue]);

    const startListening = useCallback((sid) => {
        if (sessionRef.current !== sid) return;
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
            toast.error("Su navegador no soporta reconocimiento de voz");
            setState("idle");
            setActive(false);
            return;
        }

        setState("listening");
        playBeep(); // audible "your turn" cue
        startMicAnalyser(sid); // react to the user's real voice

        const recognition = new SpeechRecognition();
        recognitionRef.current = recognition;
        recognition.lang = "es-SV";
        recognition.interimResults = false;
        recognition.maxAlternatives = 1;
        recognition.continuous = false;

        recognition.onresult = (event) => {
            const transcript = event.results[0][0].transcript;
            if (transcript.trim() && sessionRef.current === sid) sendMessage(transcript, sid);
        };

        recognition.onerror = (event) => {
            if (event.error === "no-speech" && stateRef.current === "listening" && sessionRef.current === sid) {
                try { recognition.start(); } catch (_e) { /* retry */ }
            }
        };

        recognition.onend = () => {
            if (stateRef.current === "listening" && recognitionRef.current === recognition && sessionRef.current === sid) {
                try { recognition.start(); } catch (_e) { /* retry */ }
            }
        };

        try { recognition.start(); } catch (_e) {
            toast.error("No se pudo activar el micrófono");
            setState("idle");
            setActive(false);
        }
    }, [playBeep, startMicAnalyser]);

    const stopListening = () => {
        stopMicAnalyser();
        if (recognitionRef.current) {
            try { recognitionRef.current.abort(); } catch (_e) { /* */ }
            recognitionRef.current = null;
        }
    };

    const sendMessage = async (text, sid) => {
        if (sessionRef.current !== sid) return;
        stopListening();
        setState("thinking");
        navTargetRef.current = null;
        const token = getToken();
        const controller = new AbortController();
        abortRef.current = controller;

        try {
            const resp = await fetch(`${API_BASE}/api/ai/silvia/chat`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({ message: text, branch_id: selectedBranchId || undefined }),
                signal: controller.signal,
            });

            if (!resp.ok) throw new Error("Error del servidor");

            const reader = resp.body.getReader();
            const decoder = new TextDecoder();
            let fullText = "";
            let sentenceBuffer = "";
            const navRegex = /\[NAV:(\/[^\]]+)\]/g;
            const ticketRegex = /\[TICKET:[^\]]+\]/g;

            while (true) {
                if (sessionRef.current !== sid) break;
                const { done, value } = await reader.read();
                if (done) break;
                const chunk = decoder.decode(value, { stream: true });
                for (const line of chunk.split("\n")) {
                    if (!line.startsWith("data: ")) continue;
                    const raw = line.slice(6).trim();
                    if (raw === "[DONE]") break;
                    try {
                        const parsed = JSON.parse(raw);
                        if (parsed.error) throw new Error(parsed.error);
                        if (parsed.content) {
                            // Check for NAV tag (comes first in stream)
                            const navInChunk = parsed.content.match(/\[NAV:(\/[^\]]+)\]/);
                            if (navInChunk) {
                                navTargetRef.current = navInChunk[1];
                                continue; // don't add NAV tag to text/TTS
                            }
                            // Strip TICKET tags from displayed text
                            const ticketInChunk = parsed.content.match(/\[TICKET:[^\]]+\]/);
                            if (ticketInChunk) continue;
                            fullText += parsed.content;
                            sentenceBuffer += parsed.content;
                            const cleanBuffer = sentenceBuffer.replace(navRegex, "").replace(ticketRegex, "");
                            const match = cleanBuffer.match(/^(.*?[.!?])\s*/);
                            if (match) {
                                enqueueTTS(match[1], sid);
                                sentenceBuffer = cleanBuffer.slice(match[0].length);
                            } else {
                                sentenceBuffer = cleanBuffer;
                            }
                        }
                    } catch (_e) { /* non-JSON */ }
                }
            }

            if (sessionRef.current !== sid) return;

            // Send remaining buffer
            const remaining = sentenceBuffer.replace(navRegex, "").replace(ticketRegex, "").trim();
            if (remaining) enqueueTTS(remaining, sid);
            if (!fullText.trim()) startListening(sid);
        } catch (e) {
            if (e.name !== "AbortError") {
                toast.error("Error comunicándose con Silvia");
            }
            if (sessionRef.current === sid) {
                setState("idle");
                setActive(false);
            }
        }
    };

    const handleActivate = async () => {
        if (active) {
            killSession();
            setState("idle");
            setActive(false);
            return;
        }

        killSession(); // clean slate
        ensureAudioGraph(); // unlock AudioContext on the user gesture
        const sid = sessionRef.current; // capture current session id
        setActive(true);
        setState("greeting");
        const token = getToken();

        try {
            const resp = await fetch(`${API_BASE}/api/ai/silvia/greet`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!resp.ok) throw new Error("Error");
            if (sessionRef.current !== sid) return; // user already toggled off
            const audioBytes = await resp.arrayBuffer();
            if (sessionRef.current !== sid) return;
            audioQueueRef.current.push(audioBytes);
            setState("speaking");
            playNextInQueue(sid);
        } catch (_e) {
            if (sessionRef.current === sid) {
                toast.error("No se pudo activar a Silvia");
                setState("idle");
                setActive(false);
            }
        }
    };

    // ---- Equalizer visualizer render loop ----
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        const dpr = window.devicePixelRatio || 1;
        const CSS = 112; // matches w-28/h-28
        canvas.width = CSS * dpr;
        canvas.height = CSS * dpr;
        ctx.scale(dpr, dpr);

        const N = 56;                     // radial bars around the orb
        const heights = new Array(N).fill(0.12);
        let rot = 0;                      // rotation of the spectrum field
        let haloRot = 0;                  // rotation of the neon halo

        // Idle particle field (constellation) — Truly red/yellow over dark glass
        const PR = CSS * 0.42;            // containment radius (fills most of the orb)
        const particles = Array.from({ length: 36 }, () => {
            const a = Math.random() * Math.PI * 2;
            const rr = Math.sqrt(Math.random()) * PR; // area-uniform → spreads toward the edge
            return {
                x: Math.cos(a) * rr,
                y: Math.sin(a) * rr,
                vx: (Math.random() - 0.5) * 0.7,
                vy: (Math.random() - 0.5) * 0.7,
                r: 1 + Math.random() * 2.2,
                col: Math.random() < 0.5 ? "#FFD62B" : "#FF0000",
            };
        });

        const draw = () => {
            rafRef.current = requestAnimationFrame(draw);
            const st = stateRef.current;
            const t = performance.now() / 1000;
            const cx = CSS / 2;
            const cy = CSS / 2;
            ctx.clearRect(0, 0, CSS, CSS);

            // --- live spectrum + bass amplitude ---
            let spectrum = null;   // Silvia's voice while speaking
            let micSpec = null;    // the user's voice while listening
            let bass = 0;
            if (st === "speaking" && analyserRef.current && freqDataRef.current) {
                spectrum = freqDataRef.current;
                analyserRef.current.getByteFrequencyData(spectrum);
                let s = 0;
                for (let i = 0; i < 10; i++) s += spectrum[i];
                bass = s / 10 / 255;
            } else if (st === "listening" && micAnalyserRef.current && micDataRef.current) {
                micSpec = micDataRef.current;
                micAnalyserRef.current.getByteFrequencyData(micSpec);
                let s = 0;
                for (let i = 0; i < 10; i++) s += micSpec[i];
                bass = s / 10 / 255;
            }

            // --- Truly palette per state ---
            let cInner, cOuter, glow;
            if (st === "listening") {
                cInner = "#FFD62B"; cOuter = "#FFF3B0"; glow = "rgba(255,214,43,0.9)";
            } else if (st === "speaking" || st === "thinking" || st === "greeting") {
                cInner = "#FF0000"; cOuter = "#FFD62B"; glow = "rgba(255,60,0,0.9)";
            } else {
                cInner = "rgba(255,214,43,0.6)"; cOuter = "rgba(255,214,43,0.3)"; glow = "rgba(255,214,43,0.4)";
            }

            // rotation speed by activity
            const spin = st === "speaking" ? 0.010 : st === "listening" ? 0.007 : st === "idle" ? 0.0025 : 0.006;
            rot += spin;
            haloRot -= spin * 1.6;

            // ============ IDLE: moving particle constellation ============
            if (st === "idle") {
                ctx.save();
                ctx.translate(cx, cy);

                // connecting lines (neural-net look) — soft gray so they don't saturate
                ctx.lineWidth = 1.1;
                ctx.shadowBlur = 0;
                for (let i = 0; i < particles.length; i++) {
                    for (let j = i + 1; j < particles.length; j++) {
                        const dx = particles[i].x - particles[j].x;
                        const dy = particles[i].y - particles[j].y;
                        const d2 = dx * dx + dy * dy;
                        if (d2 < 40 * 40) {
                            const alpha = Math.max(0.12, (1 - Math.sqrt(d2) / 40) * 0.7);
                            ctx.strokeStyle = `rgba(70,70,80,${alpha})`;
                            ctx.beginPath();
                            ctx.moveTo(particles[i].x, particles[i].y);
                            ctx.lineTo(particles[j].x, particles[j].y);
                            ctx.stroke();
                        }
                    }
                }

                // update + draw glowing particles
                for (const p of particles) {
                    p.x += p.vx; p.y += p.vy;
                    p.vx += (Math.random() - 0.5) * 0.05;
                    p.vy += (Math.random() - 0.5) * 0.05;
                    p.vx *= 0.985; p.vy *= 0.985;
                    const dist = Math.hypot(p.x, p.y) || 0.0001;
                    if (dist > PR) { p.vx -= (p.x / dist) * 0.05; p.vy -= (p.y / dist) * 0.05; }
                    const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r * 2.4);
                    g.addColorStop(0, p.col);
                    g.addColorStop(1, "rgba(0,0,0,0)");
                    ctx.fillStyle = g;
                    ctx.shadowColor = p.col;
                    ctx.shadowBlur = 6;
                    ctx.beginPath();
                    ctx.arc(p.x, p.y, p.r * 2.4, 0, Math.PI * 2);
                    ctx.fill();
                }

                // soft breathing core — wider pulsation so it stands out
                const b = 0.5 + 0.5 * Math.sin(t * 1.8);
                const cr = CSS * 0.055 * (1 + b * 0.85);
                const cg = ctx.createRadialGradient(0, 0, 0, 0, 0, cr * 2.4);
                cg.addColorStop(0, "rgba(255,255,255,0.9)");
                cg.addColorStop(0.4, "#FFD62B");
                cg.addColorStop(1, "rgba(255,0,0,0)");
                ctx.fillStyle = cg;
                ctx.shadowColor = "rgba(255,214,43,0.8)";
                ctx.shadowBlur = 18;
                ctx.beginPath();
                ctx.arc(0, 0, cr * 2.4, 0, Math.PI * 2);
                ctx.fill();

                ctx.restore();
                return; // skip active spectrum visualization
            }

            // ============ rotating neon halo ring ============
            const haloR = CSS * 0.40;
            ctx.save();
            ctx.translate(cx, cy);
            ctx.rotate(haloRot);
            let haloStroke = glow;
            try {
                const cg = ctx.createConicGradient(0, 0, 0);
                cg.addColorStop(0.0, "rgba(0,0,0,0)");
                cg.addColorStop(0.25, cInner);
                cg.addColorStop(0.5, cOuter);
                cg.addColorStop(0.75, cInner);
                cg.addColorStop(1.0, "rgba(0,0,0,0)");
                haloStroke = cg;
            } catch (_e) { /* conic gradient unsupported — solid fallback */ }
            ctx.beginPath();
            ctx.arc(0, 0, haloR, 0, Math.PI * 2);
            ctx.lineWidth = 2;
            ctx.strokeStyle = haloStroke;
            ctx.shadowColor = glow;
            ctx.shadowBlur = 10;
            ctx.stroke();
            ctx.restore();

            // ============ radial spectrum bars ============
            const baseR = CSS * 0.205;    // inner radius where bars begin
            const maxLen = CSS * 0.135;   // outward reach
            const half = N / 2;
            ctx.save();
            ctx.translate(cx, cy);
            ctx.rotate(rot);
            ctx.lineCap = "round";
            ctx.lineWidth = 2.4;
            ctx.shadowBlur = 6;
            for (let i = 0; i < N; i++) {
                // mirror across an axis for organic symmetry
                const idx = i < half ? i : N - i;
                let target;
                if (spectrum) {
                    const usable = Math.floor(spectrum.length * 0.55);
                    const bin = Math.floor((idx / half) * usable);
                    target = Math.max(0.10, Math.min(1, (spectrum[bin] / 255) * 1.4));
                } else if (micSpec) {
                    const usable = Math.floor(micSpec.length * 0.55);
                    const bin = Math.floor((idx / half) * usable);
                    target = Math.max(0.10, Math.min(1, (micSpec[bin] / 255) * 1.7));
                } else if (st === "listening") {
                    target = 0.28 + 0.34 * (0.5 + 0.5 * Math.sin(t * 3 + idx * 0.5));
                } else if (st === "thinking" || st === "greeting") {
                    target = 0.22 + 0.26 * (0.5 + 0.5 * Math.sin(t * 5 - idx * 0.6));
                } else {
                    target = 0.12 + 0.06 * (0.5 + 0.5 * Math.sin(t * 1.5 + idx * 0.4));
                }
                heights[i] += (target - heights[i]) * 0.4; // smoothing
                const len = Math.max(CSS * 0.02, heights[i] * maxLen);
                const ang = (i / N) * Math.PI * 2;
                const c = Math.cos(ang), s = Math.sin(ang);
                const x1 = c * baseR, y1 = s * baseR;
                const x2 = c * (baseR + len), y2 = s * (baseR + len);
                const g = ctx.createLinearGradient(x1, y1, x2, y2);
                g.addColorStop(0, cInner);
                g.addColorStop(1, cOuter);
                ctx.strokeStyle = g;
                ctx.shadowColor = glow;
                ctx.beginPath();
                ctx.moveTo(x1, y1);
                ctx.lineTo(x2, y2);
                ctx.stroke();
            }
            ctx.restore();

            // ============ pulsing luminous core ============
            const breathe = 0.5 + 0.5 * Math.sin(t * (st === "idle" ? 1.6 : 3));
            const coreBase = CSS * 0.11;
            const coreR = coreBase * (1 + ((spectrum || micSpec) ? bass * 0.9 : breathe * 0.18));
            ctx.save();
            const core = ctx.createRadialGradient(cx, cy, 0, cx, cy, coreR);
            core.addColorStop(0, "#FFFFFF");
            core.addColorStop(0.35, cOuter);
            core.addColorStop(1, cInner);
            ctx.fillStyle = core;
            ctx.shadowColor = glow;
            ctx.shadowBlur = 18;
            ctx.beginPath();
            ctx.arc(cx, cy, coreR, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
        };

        draw();
        return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
    }, []);

    const stateLabels = {
        idle: "", greeting: "Conectando...", listening: "Habla ahora...",
        thinking: "Procesando...", speaking: "Silvia habla...",
    };

    const isListening = state === "listening";
    const glowColor = {
        idle: "rgba(255,214,43,0.30)",
        greeting: "rgba(255,0,0,0.50)",
        listening: "rgba(255,214,43,0.65)",
        thinking: "rgba(255,0,0,0.55)",
        speaking: "rgba(255,0,0,0.60)",
    }[state];

    return (
        <div
            className="fixed bottom-6 right-6 z-[60]"
            data-testid="silvia-button-container"
        >
            {/* Status label */}
            {active && stateLabels[state] && (
                <div
                    className={`absolute left-1/2 -translate-x-1/2 whitespace-nowrap font-semibold px-3 py-1 rounded-full backdrop-blur transition-all duration-300 ${
                        isListening
                            ? "-top-10 bg-[#FFD62B] text-zinc-900 text-xs shadow-lg shadow-yellow-400/30"
                            : "-top-8 bg-black/80 text-white text-[10px]"
                    }`}
                    data-testid="silvia-status"
                >
                    {isListening && (
                        <span className="inline-block w-2 h-2 bg-red-500 rounded-full mr-1.5 animate-pulse" />
                    )}
                    {stateLabels[state]}
                </div>
            )}

            <button
                onClick={handleActivate}
                className={`relative w-[101px] h-[101px] rounded-full overflow-hidden backdrop-blur-sm transition-transform duration-300 ${
                    active ? "scale-110" : "hover:scale-105"
                }`}
                style={{
                    background: active
                        ? "radial-gradient(circle at 50% 45%, rgba(18,18,24,0.12), rgba(18,18,24,0.04) 62%, transparent 74%)"
                        : "radial-gradient(circle at 50% 45%, rgba(8,8,12,0.30), rgba(8,8,12,0.12) 58%, transparent 78%)",
                    boxShadow: `0 0 ${active ? 34 : 18}px ${glowColor}`,
                }}
                data-testid="silvia-button"
                title={active ? "Desactivar Silvia" : "Hablar con Silvia"}
            >
                {/* faint glass rim + top sheen for depth */}
                <div
                    className="absolute inset-0 rounded-full pointer-events-none"
                    style={{
                        boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.08)",
                        background: "radial-gradient(circle at 50% 30%, rgba(255,255,255,0.07), transparent 55%)",
                    }}
                />
                <canvas
                    ref={canvasRef}
                    className="relative w-full h-full"
                    data-testid="silvia-visualizer"
                    aria-label="Visualizador de voz de Silvia"
                />
            </button>
        </div>
    );
}
